import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LangText } from '@/components'
import classNames from 'classnames'
import { ConfigProvider, Empty, Pagination, Spin } from 'antd'
import {
  apiMyCouponPage,
  CouponSortTypeEnum,
  type MiniMyCouponResponse,
  parseReceiveDetailStatus,
  UserCouponStatusEnum,
  type UserCouponStatusEnumCode,
} from '@/service/api/couponPage'
import MineCouponCard from '../components/MineCouponCard'

const PAGE_SIZE = 9

type StatusTab = 'all' | UserCouponStatusEnumCode

const resolveDisplayStatus = (
  row: MiniMyCouponResponse,
  tab: StatusTab
): UserCouponStatusEnumCode => {
  if (tab !== 'all') return tab
  return parseReceiveDetailStatus(row.receiveDetail?.status)
}

const MineCoupon = () => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<StatusTab>('all')
  const [rows, setRows] = useState<MiniMyCouponResponse[]>([])
  const [total, setTotal] = useState(0)
  const pageForApi = useRef(1)
  const [pageUi, setPageUi] = useState(1)
  const controllerRef = useRef<AbortController>(new AbortController())

  const tabOptions = useMemo(
    () =>
      [
        { key: 'all' as const, lngKey: 'b0569342' },
        { key: UserCouponStatusEnum.UNUSED, lngKey: 'f9b74618' },
        { key: UserCouponStatusEnum.USED, lngKey: '54188f2e' },
        { key: UserCouponStatusEnum.EXPIRED, lngKey: 'db690fa9' },
      ] as const,
    []
  )

  const fetchList = useCallback(() => {
    setLoading(true)
    controllerRef.current.abort()
    controllerRef.current = new AbortController()
    const statusFilter: UserCouponStatusEnumCode[] | undefined =
      activeTab === 'all' ? undefined : [activeTab]
    apiMyCouponPage(
      {
        current: pageForApi.current,
        size: PAGE_SIZE,
        couponUseStatus: statusFilter,
        sortType: CouponSortTypeEnum.DEFAULT,
      },
      { signal: controllerRef.current.signal }
    )
      .then(res => {
        if (res.code === 0) {
          setRows(res.data.records || [])
          setTotal(res.data.total || 0)
        } else {
          setRows([])
          setTotal(0)
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }, [activeTab])

  useEffect(() => {
    pageForApi.current = 1
    setPageUi(1)
    fetchList()
  }, [activeTab, fetchList])

  return (
    <div className='min-h-screen bg-[#F5F5F5] pb-16'>
      <div className='w-8xl mx-auto px-10 py-10'>
        <LangText
          lngKey='65d24316'
          className='mb-8 block text-[30px] font-bold text-black'
        >
          {t('65d24316')}
        </LangText>

        <div className='rounded-[12px] bg-white px-6 py-5 shadow-[0_2px_20px_0_rgba(30,34,46,0.05)]'>
          <div className='flex flex-wrap gap-2'>
            {tabOptions.map(opt => (
              <button
                key={opt.key}
                type='button'
                className={classNames(
                  'h-9 min-w-[88px] rounded-full px-5 text-[15px] transition-colors',
                  activeTab === opt.key
                    ? 'bg-[#EFEFFF] font-medium text-[#5751FF]'
                    : 'bg-[#F5F5F5] text-[#333333] hover:bg-[#EEEEEE]'
                )}
                onClick={() => {
                  setActiveTab(opt.key)
                }}
              >
                <LangText lngKey={opt.lngKey}>{t(opt.lngKey)}</LangText>
              </button>
            ))}
          </div>
        </div>

        <div className='mt-6'>
          {loading ? (
            <div className='flex h-[320px] items-center justify-center'>
              <Spin />
            </div>
          ) : rows.length ? (
            <div className='grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3'>
              {rows.map((row, index) => (
                <MineCouponCard
                  key={`${row.couponDetail?.couponId ?? index}-${row.receiveDetail?.couponCode ?? index}`}
                  row={row}
                  status={resolveDisplayStatus(row, activeTab)}
                />
              ))}
            </div>
          ) : (
            <div className='flex h-[320px] items-center justify-center rounded-[12px] bg-white'>
              <Empty description={t('337e26e6')} />
            </div>
          )}
        </div>

        {total > 0 ? (
          <div className='mt-10 flex justify-center'>
            <ConfigProvider
              theme={{
                components: {
                  Pagination: {
                    colorPrimary: '#5A55FF',
                    itemActiveBg: '#5A55FF',
                    itemActiveColor: '#ffffff',
                    itemActiveColorHover: '#ffffff',
                    borderRadius: 8,
                  },
                },
              }}
            >
              <Pagination
                current={pageUi}
                pageSize={PAGE_SIZE}
                total={total}
                showSizeChanger={false}
                onChange={page => {
                  pageForApi.current = page
                  setPageUi(page)
                  fetchList()
                }}
              />
            </ConfigProvider>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default MineCoupon
